import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import UsageMeter from '../components/UsageMeter';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_API_URL || '';

export default function Dashboard() {
  const { t, dir } = useLang();
  const { isAuthenticated, user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [usage, setUsage]     = useState(null);
  const [history, setHistory] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  const D = t.dashboard;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      toast.success(D.upgraded);
    }
  }, [D.upgraded, searchParams]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/api/auth/usage`, { headers }),
      axios.get(`${API}/api/auth/history`, { headers }),
    ])
      .then(([usageRes, historyRes]) => {
        setUsage(usageRes.data);
        setHistory(historyRes.data);
      })
      .catch(() => toast.error(t.errors.serverError))
      .finally(() => setFetching(false));
  }, [token, t.errors.serverError]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/payments/checkout`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t.errors.serverError);
      setUpgrading(false);
    }
  };

  if (authLoading || fetching) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--primary)' }}>
      <Header />
      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 24px', direction: dir }}>

        {/* Welcome */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#f0f0f5' }}>
            {D.welcome}, {user?.name} 👋
          </h1>
          <p style={{ color: '#9ca3af', marginTop: '4px' }}>
            {D.plan}: <span style={{
              color: user?.plan === 'pro' ? '#f0a500' : '#00d4aa',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}>{user?.plan}</span>
          </p>
        </div>

        {/* Usage meter */}
        {usage && <UsageMeter usage={usage} t={t} />}

        {/* Upgrade button for free users */}
        {user?.plan === 'free' && (
          <div style={{ margin: '24px 0' }}>
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              style={{
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #f0a500, #e67e00)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 20px rgba(240,165,0,0.4)',
              }}
            >
              {upgrading ? <><span className="spinner" /> ...</> : <>⭐ {D.upgrade}</>}
            </button>
          </div>
        )}

        {/* Conversion History */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '32px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#f0f0f5', marginBottom: '20px' }}>
            📋 {D.history}
          </h2>

          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {D.noHistory}
              <br />
              <Link to="/app" style={{ color: '#e94560', textDecoration: 'none', fontWeight: 600, marginTop: '12px', display: 'inline-block' }}>
                {t.nav.app} →
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Mode', 'File', 'Lang', 'Engine', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', color: '#6b7280', fontWeight: 600, textAlign: 'start' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '12px', color: '#f0f0f5' }}>
                        {D.modes[r.mode] || r.mode}
                      </td>
                      <td style={{ padding: '12px', color: '#9ca3af', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.filename}
                      </td>
                      <td style={{ padding: '12px', color: '#9ca3af' }}>
                        {r.source_lang && r.target_lang ? `${r.source_lang} → ${r.target_lang}` : '—'}
                      </td>
                      <td style={{ padding: '12px', color: '#9ca3af' }}>{r.engine || '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: r.success ? 'rgba(0,212,170,0.12)' : 'rgba(233,69,96,0.12)',
                          color: r.success ? '#00d4aa' : '#e94560',
                          border: `1px solid ${r.success ? 'rgba(0,212,170,0.3)' : 'rgba(233,69,96,0.3)'}`,
                        }}>
                          {r.success ? '✓' : '✗'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
